import React from 'react';
import { Input, Space, Button, Modal, message, Spin } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined, FilterOutlined } from '@ant-design/icons';
import CommonTable from 'components/CommonTable';
import type { FaxContact } from 'types/eFax';
import { CONTACT_TAG_OPTIONS } from 'types/eFax';
import AddContactModal from '../AddContactModal';
import s from '../s.module.less';
import { formatUTCTime } from 'utils/common';
import { formatFaxNumber } from 'utils/eFaxScheduleRows';

interface Props {
    contacts: FaxContact[];
    loading?: boolean;
    onAdd: (contact: FaxContact) => void | Promise<void>;
    onDelete: (id: string) => void | Promise<void>;
    onUpdate: (contact: FaxContact) => void | Promise<void>;
}

const PAGE_SIZE = 20;

const ContactsTab: React.FC<Props> = ({ contacts, loading, onAdd, onDelete, onUpdate }) => {
    const [showModal, setShowModal] = React.useState(false);
    const [editingContact, setEditingContact] = React.useState<FaxContact | undefined>(undefined);
    const [searchName, setSearchName] = React.useState('');
    const [searchNumber, setSearchNumber] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);

    const filteredContacts = React.useMemo(() => {
        let result = contacts;
        if (searchName) {
            const lower = searchName.toLowerCase();
            result = result.filter((c) => c.name.toLowerCase().includes(lower));
        }
        if (searchNumber) {
            result = result.filter((c) => c.faxNumber.includes(searchNumber));
        }
        return result;
    }, [contacts, searchName, searchNumber]);

    React.useEffect(() => {
        setCurrentPage(1);
    }, [searchName, searchNumber]);

    const makeSearchFilterDropdown = (value: string, setValue: (v: string) => void) => {
        return () => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="Search..."
                    style={{ marginBottom: 8, display: 'block' }}
                    onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                />
                <Space>
                    <Button type="primary" icon={<SearchOutlined />} size="small" style={{ width: 90 }}>
                        Search
                    </Button>
                    <Button onClick={() => setValue('')} size="small" style={{ width: 90 }}>
                        Reset
                    </Button>
                </Space>
            </div>
        );
    };

    const handleDelete = (record: FaxContact) => {
        Modal.confirm({
            title: 'Delete contact',
            content: `Are you sure you want to delete "${record.name}"?`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                await onDelete(record.id);
                message.success('Contact deleted');
            },
        });
    };

    const handleEdit = (record: FaxContact) => {
        setEditingContact(record);
        setShowModal(true);
    };

    const handleSave = async (contact: FaxContact) => {
        if (editingContact) {
            await onUpdate(contact);
            message.success('Contact updated');
            setEditingContact(undefined);
        } else {
            await onAdd(contact);
        }
    };

    const columns: ColumnsType<FaxContact> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            width: 250,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(searchName, setSearchName),
        },
        {
            title: 'FAX NUMBER',
            dataIndex: 'faxNumber',
            width: 200,
            filterIcon: <SearchOutlined />,
            filterDropdown: makeSearchFilterDropdown(searchNumber, setSearchNumber),
            render: (fax: string) => formatFaxNumber(fax) || fax || '-',
        },
        {
            title: 'TAG',
            dataIndex: 'tag',
            width: 160,
            filterIcon: <FilterOutlined style={{ fontSize: 14 }} />,
            filters: CONTACT_TAG_OPTIONS.map((t) => ({ text: t, value: t })),
            onFilter: (value, record) => record.tag === value,
            render: (tag: string) => (tag ? <span className={s.badgeTag}>{tag}</span> : '-'),
        },
        {
            title: '',
            key: 'delete',
            width: 80,
            render: (_, record) => (
                <span className={s.deleteLink} onClick={() => handleDelete(record)}>
                    Delete
                </span>
            ),
        },
        {
            title: '',
            key: 'edit',
            width: 60,
            render: (_, record) => (
                <span className={s.editLink} onClick={() => handleEdit(record)}>
                    Edit
                </span>
            ),
        },
    ];

    return (
        <>
            <div className={s.actionBar}>
                <Button
                    type="primary"
                    onClick={() => {
                        setEditingContact(undefined);
                        setShowModal(true);
                    }}
                >
                    Add contact
                </Button>
            </div>
            <Spin spinning={!!loading}>
                <CommonTable<FaxContact>
                    data={filteredContacts}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: currentPage,
                        pageSize: PAGE_SIZE,
                        total: filteredContacts.length,
                        onChange: setCurrentPage,
                        showSizeChanger: false,
                    }}
                    locale={{
                        emptyText: <div className={s.emptyText}>No contacts yet</div>,
                    }}
                />
            </Spin>
            <AddContactModal
                open={showModal}
                onClose={() => {
                    setShowModal(false);
                    setEditingContact(undefined);
                }}
                onSave={handleSave}
                editingContact={editingContact}
            />
        </>
    );
};

export default ContactsTab;
